import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";

const FIXED_LABELS = {
  copy_pix: "Copiar código PIX",
  copy_digitable_line: "Copiar linha digitável",
};

const FIXED_VALUES = {
  copy_pix: "{{Código_PIX}}",
  copy_digitable_line: "{{Linha_Digitável}}",
};

// Limites (ajuste se precisar)
const MAX_OPEN_URL = 1;      // normalmente 1 URL
const MAX_REPLY_LIKE = 3;    // quick replies (incluindo os "copy")
const COPY_TYPES = ["copy_pix", "copy_digitable_line"];
const OPT_OUT_TYPE = "opt_out_url";
const LINK_TYPES = ["open_url", OPT_OUT_TYPE];
const REGULAR_TYPES = ["quick_reply", ...LINK_TYPES];
const ALL_BUTTON_TYPES = ["quick_reply", "open_url", "copy_pix", "copy_digitable_line"];

const ButtonConfigModal = ({
  button,
  existingButtons = [],
  allowedButtonTypes,
  onClose,
  onSave,
}) => {
  const visibleButtonTypes = Array.isArray(allowedButtonTypes) && allowedButtonTypes.length
    ? allowedButtonTypes
    : ALL_BUTTON_TYPES;
  const defaultType = visibleButtonTypes.includes(button.type)
    ? button.type
    : visibleButtonTypes[0] || "quick_reply";

  const [type, setType] = useState(defaultType);
  const [label, setLabel] = useState(button.label || "");
  const [url, setUrl] = useState(button.url || "");

  const otherButtons = existingButtons.filter((b) => b.id !== button.id);

  // Contadores por tipo (sem bloquear mistura)
  const openUrlCount = otherButtons.filter((b) => b.type === "open_url").length;
  const quickReplyCount = otherButtons.filter((b) => b.type === "quick_reply").length;

  // Considera copy_pix e copy_digitable_line como "reply-like"
  const replyLikeCount =
    quickReplyCount +
    otherButtons.filter((b) => b.type === "copy_pix").length +
    otherButtons.filter((b) => b.type === "copy_digitable_line").length;

  // ✅ regra: pode 1 de cada, mas não pode repetir o mesmo tipo
  const hasCopyPix = otherButtons.some((b) => b.type === "copy_pix");
  const hasCopyLine = otherButtons.some((b) => b.type === "copy_digitable_line");
  const hasLinkButton = otherButtons.some((b) => LINK_TYPES.includes(b.type));
  const hasCopyButtons = otherButtons.some((b) => COPY_TYPES.includes(b.type));
  const hasRegularButtons = otherButtons.some((b) => REGULAR_TYPES.includes(b.type));

  const isCopyType = type === "copy_pix" || type === "copy_digitable_line";
  const isLinkType = LINK_TYPES.includes(type);

  // Se selecionar tipo copy, força label padrão (sem mudar payload)
  useEffect(() => {
    if (isCopyType) setLabel(FIXED_LABELS[type]);
  }, [type, isCopyType]);

  const handleSave = () => {
    // Fail-safe: bloqueia salvar se já existir outro botão do mesmo tipo
    if (type === "copy_pix" && hasCopyPix) return;
    if (type === "copy_digitable_line" && hasCopyLine) return;
    if (isLinkType && hasLinkButton) return;

    // Fail-safe: não permite misturar grupos
    if (isCopyType && hasRegularButtons) return;
    if (!isCopyType && hasCopyButtons) return;

    // Fail-safe: limites gerais
    if (type === "open_url" && openUrlCount >= MAX_OPEN_URL) return;
    if (type === "quick_reply" && replyLikeCount >= MAX_REPLY_LIKE) return;

    onSave({
      ...button,
      type,
      label,
      url: type === "open_url" ? url : undefined,
    });
  };

  const saveDisabled =
    (type === "open_url" && !url) ||
    (type === "copy_pix" && hasCopyPix) ||
    (type === "copy_digitable_line" && hasCopyLine) ||
    (isLinkType && hasLinkButton) ||
    (isCopyType && hasRegularButtons) ||
    (!isCopyType && hasCopyButtons) ||
    (type === "open_url" && openUrlCount >= MAX_OPEN_URL) ||
    (type === "quick_reply" && replyLikeCount >= MAX_REPLY_LIKE);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>Edição do botão</DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 2,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <TextField
          select
          label="Tipo"
          value={type}
          onChange={(e) => {
            const nextType = e.target.value;

            // trava: não permite selecionar copy repetido
            if (nextType === "copy_pix" && hasCopyPix) return;
            if (nextType === "copy_digitable_line" && hasCopyLine) return;
            if (LINK_TYPES.includes(nextType) && hasLinkButton) return;

            // trava: não mistura copy com quick_reply/open_url
            const nextIsCopyType = COPY_TYPES.includes(nextType);
            if (nextIsCopyType && hasRegularButtons) return;
            if (!nextIsCopyType && hasCopyButtons) return;

            // trava: limite de URL
            if (nextType === "open_url" && openUrlCount >= MAX_OPEN_URL && type !== "open_url") return;

            // trava: limite de botões "reply-like"
            if (nextType === "quick_reply" && replyLikeCount >= MAX_REPLY_LIKE && type !== "quick_reply") return;

            setType(nextType);

            // limpeza básica para não carregar url indevida
            if (nextType !== "open_url") setUrl("");
          }}
          sx={{ mt: 2 }}
        >
          {visibleButtonTypes.includes("quick_reply") && (
            <MenuItem
              value="quick_reply"
              disabled={
                (replyLikeCount >= MAX_REPLY_LIKE && type !== "quick_reply") ||
                hasCopyButtons
              }
            >
              Resposta rápida
            </MenuItem>
          )}

          {visibleButtonTypes.includes("open_url") && (
            <MenuItem
              value="open_url"
              disabled={
                (hasLinkButton && type !== "open_url") ||
                (openUrlCount >= MAX_OPEN_URL && type !== "open_url") ||
                hasCopyButtons
              }
            >
              Abrir página
            </MenuItem>
          )}

          {visibleButtonTypes.includes(OPT_OUT_TYPE) && (
            <MenuItem
              value={OPT_OUT_TYPE}
              disabled={hasLinkButton && type !== OPT_OUT_TYPE}
            >
              Link de descadastro
            </MenuItem>
          )}

          {visibleButtonTypes.includes("copy_pix") && (
            <MenuItem
              value="copy_pix"
              disabled={(hasCopyPix && type !== "copy_pix") || hasRegularButtons}
            >
              Copia e cola PIX
            </MenuItem>
          )}

          {visibleButtonTypes.includes("copy_digitable_line") && (
            <MenuItem
              value="copy_digitable_line"
              disabled={
                (hasCopyLine && type !== "copy_digitable_line") || hasRegularButtons
              }
            >
              Copia linha digitável
            </MenuItem>
          )}
        </TextField>

        {/* Mostra sempre o label, mas desabilita nos tipos copy (texto fixo do WhatsApp) */}
        <TextField
          label="Texto do botão"
          value={label}
          inputProps={{ maxLength: 20 }}
          helperText={isCopyType ? "Texto padrão do WhatsApp" : `${label.length}/20`}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isCopyType}
        />

        {/* Campo só visual (não mexe no payload) */}
        {isCopyType && (
          <TextField
            label="Valor"
            value={FIXED_VALUES[type]}
            disabled
            helperText="Valor preenchido automaticamente pelo sistema"
          />
        )}

        {type === "open_url" && (
          <TextField
            label="Link"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saveDisabled}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ButtonConfigModal;
